/**
 * HealthSection — the CAP Agent's closure-readiness panel.
 *
 * Shows the deterministic readiness score + band, the five CMS-defensible signals
 * with severity, deadline risk, and the agent's plain-language summary and
 * prioritized recommendations. "Assess readiness" runs the agent (progress renders
 * in the shared Activity panel). Read-only surface — it never mutates the CAP.
 */
import React, { useState, useEffect, useCallback } from 'react';
import SectionCard from './SectionCard';

const API = process.env.REACT_APP_API_URL;

const BAND = {
  closure_ready: { label: 'Closure ready', ring: 'ring-emerald-200', bg: 'bg-emerald-50', text: 'text-emerald-700', bar: '#059669' },
  on_track:      { label: 'On track',      ring: 'ring-emerald-200', bg: 'bg-emerald-50', text: 'text-emerald-700', bar: '#059669' },
  at_risk:       { label: 'At risk',       ring: 'ring-amber-200',   bg: 'bg-amber-50',   text: 'text-amber-700',   bar: '#d97706' },
  not_ready:     { label: 'Not ready',     ring: 'ring-red-200',     bg: 'bg-red-50',     text: 'text-red-700',     bar: '#dc2626' },
};
const SEV_DOT = { high: 'bg-red-500', medium: 'bg-amber-500', ok: 'bg-emerald-500' };
const STATUS_LABEL = { ok: 'Met', weak: 'Needs work', gap: 'Gap' };
const STATUS_TEXT = { ok: 'text-emerald-700', weak: 'text-amber-700', gap: 'text-red-700' };
const PRI = { high: 'bg-red-50 text-red-700 ring-red-200', medium: 'bg-amber-50 text-amber-700 ring-amber-200', low: 'bg-slate-50 text-slate-600 ring-slate-200' };

export default function HealthSection({ data, onRefresh }) {
  const capId = data?.cap?.id;
  const [snapshot, setSnapshot] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState(null);
  const [draft, setDraft] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [applying, setApplying] = useState(false);

  const runDraft = async () => {
    setDrafting(true); setErr(null); setDraft(null);
    try {
      const res = await fetch(`${API}/api/pcap/caps/${capId}/agent/draft`, { method: 'POST', credentials: 'include' });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'Draft failed');
      setDraft(d.draft);
    } catch (e) { setErr(e.message); }
    finally { setDrafting(false); }
  };

  const applyDraft = async () => {
    if (!draft) return;
    setApplying(true); setErr(null);
    try {
      let rootCauseId = null;
      if (draft.root_cause?.text) {
        const rcRes = await fetch(`${API}/api/pcap/caps/${capId}/root-causes`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: draft.root_cause.text, category: draft.root_cause.category || 'process' }),
        });
        const rcd = await rcRes.json();
        rootCauseId = rcd?.root_cause?.id || rcd?.id || null;
      }
      for (const a of (draft.actions || [])) {
        const target = new Date(Date.now() + (a.target_date_offset_days || 30) * 86400000).toISOString().slice(0, 10);
        const measure = a.regulation ? `${a.measure_text || ''}${a.measure_text ? ' ' : ''}(satisfies ${a.regulation})` : a.measure_text;
        await fetch(`${API}/api/pcap/caps/${capId}/milestones`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ kind: a.kind, description: a.description, measure_text: measure, owner_name: a.owner_role || null, target_date: target, root_cause_id: rootCauseId, ai_assisted: true }),
        });
      }
      setDraft(null);
      onRefresh?.();
    } catch (e) { setErr(e.message); }
    finally { setApplying(false); }
  };

  const loadLatest = useCallback(async () => {
    if (!capId) return;
    try {
      const res = await fetch(`${API}/api/pcap/caps/${capId}/agent/health`, { credentials: 'include' });
      const d = await res.json();
      if (res.ok && d.success) setSnapshot(d.snapshot);
    } catch { /* non-fatal */ }
  }, [capId]);
  useEffect(() => { loadLatest(); }, [loadLatest]);

  const run = async () => {
    setRunning(true); setErr(null);
    try {
      const res = await fetch(`${API}/api/pcap/caps/${capId}/agent/health`, { method: 'POST', credentials: 'include' });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'Assessment failed');
      setAssessment(d.assessment);
      loadLatest();
      onRefresh?.();
    } catch (e) { setErr(e.message); }
    finally { setRunning(false); }
  };

  // Prefer a fresh assessment; else the latest persisted snapshot.
  const view = assessment || (snapshot && {
    score: snapshot.readiness_score, band: snapshot.readiness_band, can_close: snapshot.can_close,
    signals: snapshot.signals || [], deadlines: snapshot.deadlines || {},
    summary: snapshot.ai_summary, recommendations: snapshot.ai_recommendations || [],
  });
  const band = BAND[view?.band] || BAND.not_ready;
  const asOf = !assessment && snapshot?.created_at ? new Date(snapshot.created_at).toLocaleString() : null;

  return (
    <SectionCard
      title={<span className="inline-flex items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-white text-[10px] font-semibold px-2 py-0.5">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> CAP Agent
        </span>
        <span>Closure readiness</span>
      </span>}
      hint="An AI agent reviews the whole CAP and scores closure readiness against CMS's defensible-CAP criteria — root cause, remediation (corrective and preventive), evidence, effectiveness validation, and deadlines. It can also draft the remediation for you."
      accent={
        <div className="flex items-center gap-2">
          <button onClick={runDraft} disabled={drafting || !capId}
            className="inline-flex items-center gap-1.5 rounded-full bg-white ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50 text-slate-700 text-[13px] font-medium px-4 py-2 transition">
            {drafting ? 'Drafting…' : 'Draft remediation'}
          </button>
          <button onClick={run} disabled={running || !capId}
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-[13px] font-medium px-4 py-2 transition">
            {running ? 'Assessing…' : view ? 'Re-assess' : 'Assess readiness'}
          </button>
        </div>
      }
    >
      {err && <div className="rounded-lg bg-red-50 ring-1 ring-red-200 p-3 text-sm text-red-800 mb-4">{err}</div>}

      {/* Regulation-grounded draft — review before applying */}
      {draft && (
        <div className="rounded-2xl ring-1 ring-orange-200 bg-orange-50/50 p-5 mb-5">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="text-[13px] font-semibold text-slate-900">Proposed remediation — review before applying</div>
            <div className="flex items-center gap-2">
              <button onClick={() => setDraft(null)} className="text-[12px] text-slate-500 hover:text-slate-800">Discard</button>
              <button onClick={applyDraft} disabled={applying}
                className="rounded-full bg-orange-600 hover:bg-orange-500 disabled:opacity-50 text-white text-[12px] font-medium px-3.5 py-1.5">
                {applying ? 'Applying…' : 'Apply to CAP'}
              </button>
            </div>
          </div>
          {draft.root_cause?.text && (
            <div className="mb-3">
              <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-400">Root cause ({draft.root_cause.category})</div>
              <p className="text-[13px] text-slate-800 mt-0.5">{draft.root_cause.text}</p>
            </div>
          )}
          <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-400 mb-1.5">Actions</div>
          <ul className="space-y-2">
            {(draft.actions || []).map((a, i) => (
              <li key={i} className="rounded-xl bg-white ring-1 ring-slate-200/80 p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ring-1 ${a.kind === 'preventive' ? 'bg-indigo-50 text-indigo-700 ring-indigo-200' : 'bg-slate-50 text-slate-600 ring-slate-200'}`}>{a.kind}</span>
                  <span className="text-[13px] font-medium text-slate-800">{a.description}</span>
                </div>
                <p className="text-[12px] text-slate-500 mt-1">Measure: {a.measure_text || '—'} · Owner: {a.owner_role || '—'} · Due in {a.target_date_offset_days}d{a.regulation ? ` · ${a.regulation}` : ''}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
      {!view ? (
        <p className="text-[13px] text-slate-500">Run the agent to see this CAP's closure readiness and the next best actions.</p>
      ) : (
        <div className="space-y-5">
          {/* Score + band */}
          <div className={`rounded-2xl ${band.bg} ring-1 ${band.ring} p-5 flex items-center gap-5`}>
            <div className="relative shrink-0" style={{ width: 66, height: 66 }}>
              <svg width="66" height="66" viewBox="0 0 66 66">
                <circle cx="33" cy="33" r="28" fill="none" stroke="#e2e8f0" strokeWidth="7" />
                <circle cx="33" cy="33" r="28" fill="none" stroke={band.bar} strokeWidth="7" strokeLinecap="round"
                  strokeDasharray={`${(view.score / 100) * 2 * Math.PI * 28} ${2 * Math.PI * 28}`} transform="rotate(-90 33 33)" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center text-[18px] font-bold text-slate-900">{view.score}</div>
            </div>
            <div className="min-w-0">
              <div className={`text-[15px] font-semibold ${band.text}`}>{band.label}{view.can_close ? ' · clears the closure bar' : ''}</div>
              {view.summary && <p className="text-[13px] text-slate-600 mt-1 leading-relaxed">{view.summary}</p>}
              {asOf && <p className="text-[11px] text-slate-400 mt-1">Last assessed {asOf}</p>}
            </div>
          </div>

          {/* Signals */}
          <div className="grid sm:grid-cols-2 gap-2">
            {(view.signals || []).map((s) => (
              <div key={s.key} className="rounded-xl ring-1 ring-slate-200/80 p-3.5 flex gap-3">
                <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${SEV_DOT[s.severity] || SEV_DOT.ok}`} />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-semibold text-slate-800">{s.label}</span>
                    <span className={`text-[11px] font-medium ${STATUS_TEXT[s.status] || 'text-slate-500'}`}>{STATUS_LABEL[s.status] || s.status}</span>
                  </div>
                  <p className="text-[12px] text-slate-500 mt-0.5 leading-relaxed">{s.detail}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Recommendations */}
          {(view.recommendations || []).length > 0 && (
            <div>
              <div className="text-[12px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Recommended next actions</div>
              <ol className="space-y-2">
                {view.recommendations.map((r, i) => (
                  <li key={i} className="rounded-xl ring-1 ring-slate-200/80 p-3.5">
                    <div className="flex items-start gap-2.5">
                      <span className="text-[12px] font-bold text-slate-400 mt-0.5">{i + 1}</span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[13px] font-semibold text-slate-800">{r.title}</span>
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ring-1 ${PRI[r.priority] || PRI.low}`}>{r.priority}</span>
                        </div>
                        {r.why && <p className="text-[12px] text-slate-500 mt-0.5 leading-relaxed">{r.why}</p>}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}
